import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import {
  HIDE_TABLE_COLUMNS,
  REORDER_TABLE_COLUMNS,
  RESIZE_TABLE_COLUMNS,
  TableColumnSettingsKey,
} from 'models/User';
import {
  applyReorderedVisibleColumns,
  mergeColumnOrder,
  getIsDefaultColumns,
  getColumnSettings,
} from '../utils';
import { ColumnSizing } from '../types';
import { EMPTY_ARRAY, LONGER_DEBOUNCE_TIME } from 'data/global';

const EMPTY_SIZING: ColumnSizing = {};

// `tableName` is optional so a table that only sometimes persists its columns
// (the members table does it buy-side but not in the waiting room) can call
// this unconditionally: without it, the default columns are returned as-is and
// nothing is saved.
export default function useTableColumnSettings<T extends string>(
  tableName: TableColumnSettingsKey | undefined,
  defaultColumns: T[],
  // Columns hidden until the user saves this table's settings for the first
  // time — an opt-in column, not one the user has chosen to hide.
  defaultHiddenColumns: string[] = EMPTY_ARRAY,
  syncHiddenColumnsFromUser = false,
) {
  const { currentUser, patchCurrentUser } = useContext(CurrentUserContext);

  const hideKey =
    tableName &&
    HIDE_TABLE_COLUMNS[tableName as keyof typeof HIDE_TABLE_COLUMNS];
  const orderKey = tableName && REORDER_TABLE_COLUMNS[tableName];
  const widthsKey = tableName && RESIZE_TABLE_COLUMNS[tableName];

  const userHiddenColumns = hideKey ? currentUser?.[hideKey] : undefined;
  const userOrderColumns = orderKey ? currentUser?.[orderKey] : undefined;
  const userSizingColumns = widthsKey ? currentUser?.[widthsKey] : undefined;

  const [hiddenColumns, setHiddenColumnsState] = useState(
    () => userHiddenColumns ?? defaultHiddenColumns,
  );

  const [pendingOrder, setPendingOrder] = useState<string[]>();

  const [columnSizing, setColumnSizing] = useState(
    () => userSizingColumns ?? EMPTY_SIZING,
  );

  const persistedOrder = useMemo(
    () => pendingOrder ?? userOrderColumns ?? EMPTY_ARRAY,
    [pendingOrder, userOrderColumns],
  );

  const clearPendingOrder = useCallback(
    (savedOrder: string[]) =>
      setPendingOrder((prev) => (prev === savedOrder ? undefined : prev)),
    [],
  );

  const fullOrder = useMemo(
    () => mergeColumnOrder(defaultColumns, persistedOrder),
    [defaultColumns, persistedOrder],
  );

  const columns = useMemo(
    () => fullOrder.filter((col) => !hiddenColumns.includes(col)) as T[],
    [fullOrder, hiddenColumns],
  );

  const isDefaultColumns = useMemo(
    () =>
      getIsDefaultColumns(
        defaultColumns,
        hiddenColumns,
        defaultHiddenColumns,
        persistedOrder,
      ),
    [defaultColumns, hiddenColumns, defaultHiddenColumns, persistedOrder],
  );

  const saveOrder = useMemo(
    () =>
      debounce(async (newFullOrder: string[]) => {
        if (!orderKey) return;

        await patchCurrentUser(
          { [orderKey]: newFullOrder },
          { skipToast: true, skipErrorToast: true },
        );
        clearPendingOrder(newFullOrder);
      }, LONGER_DEBOUNCE_TIME),
    [orderKey, patchCurrentUser, clearPendingOrder],
  );

  const saveWidths = useMemo(
    () =>
      debounce((newColumnSizing: ColumnSizing) => {
        if (!widthsKey) return;

        patchCurrentUser(
          { [widthsKey]: newColumnSizing },
          { skipToast: true, skipErrorToast: true },
        );
      }, LONGER_DEBOUNCE_TIME),
    [widthsKey, patchCurrentUser],
  );

  useEffect(
    () => () => {
      saveOrder.flush();
    },
    [saveOrder],
  );
  useEffect(() => saveWidths.flush, [saveWidths]);

  useEffect(() => {
    if (!syncHiddenColumnsFromUser || userHiddenColumns === undefined) return;

    setHiddenColumnsState((prev) =>
      isEqual(prev, userHiddenColumns) ? prev : userHiddenColumns,
    );
  }, [syncHiddenColumnsFromUser, userHiddenColumns]);

  const onReorder = useCallback(
    (newVisibleOrder: T[]) => {
      const newFullOrder = applyReorderedVisibleColumns(
        fullOrder,
        newVisibleOrder,
        hiddenColumns,
      );

      setPendingOrder(newFullOrder);
      saveOrder(newFullOrder);
    },
    [fullOrder, hiddenColumns, saveOrder],
  );

  const onColumnSizingChange = useCallback(
    (newColumnSizing: ColumnSizing) => {
      setColumnSizing(newColumnSizing);
      saveWidths(newColumnSizing);
    },
    [saveWidths],
  );

  const saveColumnSettings = useCallback(
    async (nextHiddenColumns: string[], nextColumnOrder: string[]) => {
      saveOrder.cancel();
      setPendingOrder(nextColumnOrder);
      setHiddenColumnsState(nextHiddenColumns);

      if (!tableName) return;

      await patchCurrentUser(
        getColumnSettings(hideKey, orderKey, nextHiddenColumns, nextColumnOrder),
        { skipToast: true },
      );
      clearPendingOrder(nextColumnOrder);
    },
    [
      tableName,
      hideKey,
      orderKey,
      saveOrder,
      patchCurrentUser,
      clearPendingOrder,
    ],
  );

  const resetToDefault = useCallback(
    () => saveColumnSettings(defaultHiddenColumns, EMPTY_ARRAY),
    [defaultHiddenColumns, saveColumnSettings],
  );

  return {
    columns,
    allColumns: fullOrder as T[],
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  };
}
